var __pow = Math.pow;

// src/wallet/base.ts
import EventEmitter from "eventemitter3";
var WalletReadyState = /* @__PURE__ */ ((WalletReadyState2) => {
  WalletReadyState2["Installed"] = "Installed";
  WalletReadyState2["NotDetected"] = "NotDetected";
  WalletReadyState2["Loadable"] = "Loadable";
  WalletReadyState2["Unsupported"] = "Unsupported";
  return WalletReadyState2;
})(WalletReadyState || {});
var BaseWalletAdapter = class extends EventEmitter {
  get connected() {
    return !!this.address;
  }
};
function scopePollingDetectionStrategy(detect) {
  if (typeof window === "undefined" || typeof document === "undefined")
    return;
  const disposers = [];
  function detectAndDispose() {
    const detected = detect();
    if (detected) {
      for (const dispose of disposers) {
        dispose();
      }
    }
  }
  const interval = setInterval(detectAndDispose, 1e3);
  disposers.push(() => clearInterval(interval));
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", detectAndDispose, {
      once: true
    });
    disposers.push(
      () => document.removeEventListener("DOMContentLoaded", detectAndDispose)
    );
  }
  if (document.readyState !== "complete") {
    window.addEventListener("load", detectAndDispose, { once: true });
    disposers.push(() => window.removeEventListener("load", detectAndDispose));
  }
  detectAndDispose();
}

// src/wallet/errors.ts
var WalletError = class extends Error {
  constructor(message, error) {
    super(message);
    this.error = error;
  }
};
var WalletNotReadyError = class extends WalletError {
  constructor() {
    super(...arguments);
    this.name = "WalletNotReadyError";
  }
};
var WalletLoadError = class extends WalletError {
  constructor() {
    super(...arguments);
    this.name = "WalletLoadError";
  }
};
var WalletConfigError = class extends WalletError {
  constructor() {
    super(...arguments);
    this.name = "WalletConfigError";
  }
};
var WalletConnectionError = class extends WalletError {
  constructor() {
    super(...arguments);
    this.name = "WalletConnectionError";
  }
};
var WalletDisconnectedError = class extends WalletError {
  constructor() {
    super(...arguments);
    this.name = "WalletDisconnectedError";
  }
};
var WalletDisconnectionError = class extends WalletError {
  constructor() {
    super(...arguments);
    this.name = "WalletDisconnectionError";
  }
};
var WalletAccountError = class extends WalletError {
  constructor() {
    super(...arguments);
    this.name = "WalletAccountError";
  }
};
var WalletPublicKeyError = class extends WalletError {
  constructor() {
    super(...arguments);
    this.name = "WalletPublicKeyError";
  }
};
var WalletKeypairError = class extends WalletError {
  constructor() {
    super(...arguments);
    this.name = "WalletKeypairError";
  }
};
var WalletNotConnectedError = class extends WalletError {
  constructor() {
    super(...arguments);
    this.name = "WalletNotConnectedError";
  }
};
var WalletSendTransactionError = class extends WalletError {
  constructor() {
    super(...arguments);
    this.name = "WalletSendTransactionError";
  }
};
var WalletSignMessageError = class extends WalletError {
  constructor() {
    super(...arguments);
    this.name = "WalletSignMessageError";
  }
};
var WalletSignTransactionError = class extends WalletError {
  constructor() {
    super(...arguments);
    this.name = "WalletSignTransactionError";
  }
};
var WalletTimeoutError = class extends WalletError {
  constructor() {
    super(...arguments);
    this.name = "WalletTimeoutError";
  }
};
var WalletWindowBlockedError = class extends WalletError {
  constructor() {
    super(...arguments);
    this.name = "WalletWindowBlockedError";
  }
};
var WalletWindowClosedError = class extends WalletError {
  constructor() {
    super(...arguments);
    this.name = "WalletWindowClosedError";
  }
};
var WalletNotSelectedError = class extends WalletError {
  constructor() {
    super(...arguments);
    this.name = "WalletNotSelectedError";
  }
};

// src/utils/common.ts
function truncate(str) {
  if (str == null) {
    throw new Error("truncate: String is null");
  }
  return `${str.substring(0, 6)}...${str.substring(str.length - 4)}`;
}
function toBase64(obj) {
  return Buffer.from(JSON.stringify(obj)).toString("base64");
}

// src/utils/format.ts
import BigNumber from "bignumber.js";
BigNumber.config({
  DECIMAL_PLACES: 6,
  ROUNDING_MODE: BigNumber.ROUND_DOWN,
  EXPONENTIAL_AT: [-10, 20]
});
var num = (value = "0") => {
  return new BigNumber(value);
};
var NumberFormatSpecifier = /* @__PURE__ */ ((NumberFormatSpecifier2) => {
  NumberFormatSpecifier2[NumberFormatSpecifier2["PERCENTAGE"] = 1] = "PERCENTAGE";
  NumberFormatSpecifier2[NumberFormatSpecifier2["CURRENCY"] = 2] = "CURRENCY";
  NumberFormatSpecifier2[NumberFormatSpecifier2["FLOAT"] = 3] = "FLOAT";
  return NumberFormatSpecifier2;
})(NumberFormatSpecifier || {});
function formatAmount(value, options) {
  if (value == null) {
    return "--";
  }
  const valOrZero = num(value != null ? value : 0);
  const valToFormat = (options == null ? void 0 : options.shouldDivide) ? valOrZero.div(__pow(10, 6)) : valOrZero;
  const currentDp = valToFormat.dp();
  let formatOptions = {
    prefix: "",
    decimalSeparator: ".",
    groupSeparator: ",",
    groupSize: 3,
    secondaryGroupSize: 3,
    fractionGroupSeparator: " ",
    fractionGroupSize: 0,
    suffix: ""
  };
  let dp = 2;
  if (currentDp != null) {
    if (!(currentDp > 2)) {
      dp = currentDp;
    }
  }
  if ((options == null ? void 0 : options.formatSpecifier) == 1 /* PERCENTAGE */) {
    formatOptions.suffix = "%";
  }
  if ((options == null ? void 0 : options.formatSpecifier) == 2 /* CURRENCY */) {
    formatOptions.prefix = "$";
  }
  if ((options == null ? void 0 : options.formatSpecifier) == 3 /* FLOAT */) {
    if (currentDp != null) {
      if (currentDp > 6) {
        dp = 6;
      } else {
        dp = currentDp;
      }
    }
  }
  return valToFormat.toFormat(dp, formatOptions);
}
var numberAccept = /[\d.]+/g;
function parseNumber(string) {
  return (string.match(numberAccept) || []).join("");
}
function formatFloatingPointNumber(value, maxDigits) {
  const parsed = parseNumber(value);
  const [head, tail] = parsed.split(".");
  const scaledTail = tail != null ? tail.slice(0, maxDigits) : "";
  const number = Number.parseFloat(`${head}.${scaledTail}`);
  if (Number.isNaN(number)) {
    return "";
  }
  const formatted = number.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDigits
  });
  if (parsed.includes(".")) {
    const [formattedHead] = formatted.split(".");
    const formattedTail = scaledTail !== "" && scaledTail[maxDigits - 1] === "0" ? scaledTail.slice(0, -1) : scaledTail;
    return `${formattedHead}.${formattedTail}`;
  }
  return formatted;
}
export {
  BaseWalletAdapter,
  EventEmitter,
  NumberFormatSpecifier,
  WalletAccountError,
  WalletConfigError,
  WalletConnectionError,
  WalletDisconnectedError,
  WalletDisconnectionError,
  WalletError,
  WalletKeypairError,
  WalletLoadError,
  WalletNotConnectedError,
  WalletNotReadyError,
  WalletNotSelectedError,
  WalletPublicKeyError,
  WalletReadyState,
  WalletSendTransactionError,
  WalletSignMessageError,
  WalletSignTransactionError,
  WalletTimeoutError,
  WalletWindowBlockedError,
  WalletWindowClosedError,
  formatAmount,
  formatFloatingPointNumber,
  num,
  numberAccept,
  parseNumber,
  scopePollingDetectionStrategy,
  toBase64,
  truncate
};
